import { useState, useEffect, useMemo, useRef } from "react";
import { BarChart3, Search, X, Wand2, Sparkles, Loader2, Send, ChevronDown, Check } from "lucide-react";
import { Input } from "../../components/ui/input";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";

export default function MisIntelligence({ allProperties, currentCity, onApply }) {
  const [query, setQuery] = useState("");
  // Multi-select state — replaces the old single pickedLoc grid picker.
  const [selectedLocs, setSelectedLocs] = useState([]);
  const [includeClosed, setIncludeClosed] = useState(true);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [pickedFY, setPickedFY] = useState(null);
  const [pickedMonth, setPickedMonth] = useState(null);
  const rootRef = useRef(null);

  // Close the dropdown when the user clicks outside it.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Auto-select if currentCity matches an existing MIS property (only when
  // the user hasn't manually picked anything yet).
  useEffect(() => {
    if (!currentCity || !allProperties.length) return;
    if (selectedLocs.length > 0) return;
    const exact = allProperties.find((p) => p.location?.toLowerCase() === currentCity.toLowerCase());
    if (exact) setSelectedLocs([exact.location]);
  }, [currentCity, allProperties]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch full FY/month history ONLY when exactly one location is selected
  // (FY/month drilldown is per-location; multi-select just averages summaries).
  useEffect(() => {
    if (selectedLocs.length !== 1) {
      setHistory(null); setPickedFY(null); setPickedMonth(null);
      return;
    }
    const loc = selectedLocs[0];
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/property-ic/mis-history/${encodeURIComponent(loc)}`);
        if (!cancelled) { setHistory(data); setPickedFY(null); setPickedMonth(null); }
      } catch (e) {
        logError("mis-history fetch failed", e);
        if (!cancelled) setHistory(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLocs]);

  const visible = useMemo(
    () => (includeClosed ? allProperties : allProperties.filter((p) => p.is_operational !== false)),
    [allProperties, includeClosed]
  );

  const filtered = useMemo(() => {
    if (!query) return visible;
    const q = query.toLowerCase();
    return visible.filter((p) =>
      p.location?.toLowerCase().includes(q) ||
      p.state?.toLowerCase().includes(q) ||
      p.type?.toLowerCase().includes(q)
    );
  }, [query, visible]);

  const opCount = allProperties.filter((p) => p.is_operational !== false).length;
  const closedCount = allProperties.length - opCount;
  const selectedProps = useMemo(
    () => allProperties.filter((p) => selectedLocs.includes(p.location)),
    [allProperties, selectedLocs]
  );

  const toggleLoc = (loc) =>
    setSelectedLocs((prev) => (prev.includes(loc) ? prev.filter((x) => x !== loc) : [...prev, loc]));
  const selectAll = () => setSelectedLocs(filtered.map((p) => p.location));
  const clearAll = () => setSelectedLocs([]);

  const fyMonths = useMemo(() => {
    if (!history?.months) return [];
    if (!pickedFY) return [];
    return history.months.filter((m) => m.fy === pickedFY);
  }, [history, pickedFY]);

  // Build the "displayed metrics" — averaged across selectedProps.
  // Single-select: keep FY/month drilldown (like before).
  // Multi-select: plain average of summary fields, drilldown disabled.
  const picked = useMemo(() => {
    if (selectedProps.length === 0) return null;

    // Single-selection FY/month drilldown — preserved from the previous UX.
    if (selectedProps.length === 1) {
      const baseProp = selectedProps[0];
      if (pickedMonth && history) {
        const m = history.months.find((x) => x.year === pickedMonth.year && x.month === pickedMonth.month);
        if (m) {
          const margin = (m.total_revenue || 0) - (m.total_expense || 0);
          return {
            ...baseProp,
            _scope: `${m.month_label}`,
            dorm_occ_pct: m.occupancy_dorm_pct,
            private_occ_pct: m.occupancy_private_pct,
            avg_occupancy_pct: m.occupancy_pct,
            dorm_adr: m.dorm_adr,
            private_adr_bed: m.private_adr_bed,
            private_adr: m.private_adr_room,
            avg_adr: m.adr,
            avg_revpab: m.revpab,
            non_bed_revpab: m.non_bed_revpab,
            avg_total_revenue_lakhs: Math.round((m.total_revenue || 0) / 100000 * 10) / 10,
            avg_total_expenses_lakhs: Math.round((m.total_expense || 0) / 100000 * 10) / 10,
            avg_margin_lakhs: Math.round(margin / 100000 * 10) / 10,
            avg_ebitda_lakhs: Math.round(margin / 100000 * 10) / 10,
          };
        }
      }
      if (pickedFY && history) {
        const fy = (history.fy_summary || []).find((f) => f.fy === pickedFY);
        if (fy) {
          return {
            ...baseProp,
            _scope: pickedFY,
            dorm_occ_pct: fy.dorm_occ_pct,
            private_occ_pct: fy.private_occ_pct,
            avg_occupancy_pct: fy.avg_occupancy_pct,
            dorm_adr: fy.dorm_adr,
            private_adr_bed: fy.private_adr_bed,
            private_adr: fy.private_adr_room,
            avg_adr: fy.avg_adr,
            avg_revpab: fy.avg_revpab,
            non_bed_revpab: fy.non_bed_revpab,
            avg_total_revenue_lakhs: fy.total_revenue_lakhs,
            avg_total_expenses_lakhs: fy.total_expense_lakhs,
            avg_margin_lakhs: fy.ebitda_lakhs,
            avg_ebitda_lakhs: fy.ebitda_lakhs,
          };
        }
      }
      return { ...baseProp, _scope: "Last 4 FY avg" };
    }

    // Multi-select: average each summary field across the selected properties.
    const avg = (key) => {
      const nums = selectedProps.map((p) => Number(p[key])).filter((n) => !Number.isNaN(n) && n > 0);
      if (!nums.length) return null;
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    };
    const round1 = (n) => (n == null ? null : Math.round(n * 10) / 10);
    return {
      location: `${selectedProps.length} properties`,
      _scope: "Avg across selected",
      is_multi: true,
      dorm_occ_pct: round1(avg("dorm_occ_pct")) ?? round1(avg("avg_occupancy_pct")),
      dorm_adr: Math.round(avg("dorm_adr") || avg("avg_adr") || 0) || null,
      private_occ_pct: round1(avg("private_occ_pct")),
      private_adr: Math.round(avg("private_adr") || 0) || null,
      private_adr_bed: Math.round(avg("private_adr_bed") || 0) || null,
      avg_revpab: Math.round(avg("avg_revpab") || 0) || null,
      non_bed_revpab: Math.round(avg("non_bed_revpab") || 0) || null,
      avg_total_revenue_lakhs: round1(avg("avg_total_revenue_lakhs")),
      avg_margin_lakhs: round1(avg("avg_margin_lakhs")),
      beds: selectedProps.reduce((a, b) => a + (Number(b.beds) || 0), 0),
    };
  }, [selectedProps, pickedFY, pickedMonth, history]);

  const triggerLabel =
    selectedLocs.length === 0 ? "Select locations…"
    : selectedLocs.length === 1 ? selectedLocs[0]
    : `${selectedLocs.length} locations selected`;

  return (
    <div className="rounded-lg border border-emerald-300 bg-emerald-50/40 p-3" data-testid="ic-mis-intel" ref={rootRef}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <BarChart3 className="w-4 h-4 text-emerald-700" />
        <span className="font-display font-bold text-[13px] text-emerald-900">MIS intelligence</span>
        <span className="text-[10px] text-emerald-700">{opCount} operational{closedCount ? ` + ${closedCount} closed` : ""} · last 4 FY</span>
        {closedCount > 0 && (
          <label className="ml-auto inline-flex items-center gap-1 text-[10.5px] text-emerald-800 cursor-pointer select-none" data-testid="ic-mis-include-closed-label">
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
              data-testid="ic-mis-include-closed"
              className="accent-emerald-700 w-3 h-3"
            />
            Include closed stops
          </label>
        )}
      </div>

      {/* Collapsed multi-select trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="ic-mis-dropdown-trigger"
          className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-emerald-300 bg-white text-[12px] text-emerald-900 hover:border-emerald-500 transition-colors"
        >
          <span className="truncate font-medium">{triggerLabel}</span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedLocs.length > 0 && (
              <span onClick={(e) => { e.stopPropagation(); clearAll(); }} className="text-[10px] font-medium text-emerald-700 hover:text-emerald-900 px-1.5 py-0.5 rounded hover:bg-emerald-50" data-testid="ic-mis-dropdown-clear-trigger">clear</span>
            )}
            <ChevronDown className={`w-3.5 h-3.5 text-emerald-600 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
        </button>

        {open && (
          <div className="absolute z-30 top-[calc(100%+4px)] left-0 right-0 rounded-md border border-emerald-300 bg-white shadow-lg overflow-hidden" data-testid="ic-mis-dropdown-panel">
            <div className="flex items-center gap-2 border-b border-emerald-100 p-2">
              <button
                onClick={selectAll}
                data-testid="ic-mis-select-all"
                className="text-[11px] font-semibold text-emerald-800 hover:text-emerald-900 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded"
              >
                Select all{query ? ` (${filtered.length})` : ""}
              </button>
              <button
                onClick={clearAll}
                data-testid="ic-mis-clear-all"
                className="text-[11px] font-medium text-zinc-700 hover:text-zinc-900 border border-zinc-200 px-2 py-1 rounded"
              >
                Clear all
              </button>
              <div className="ml-auto text-[10.5px] text-emerald-700">{selectedLocs.length} selected</div>
            </div>
            <div className="relative border-b border-emerald-100 p-2">
              <Search className="w-3.5 h-3.5 absolute left-4 top-4 text-emerald-600" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search property, state or type…"
                className="pl-7 h-8 rounded-md border-emerald-200 bg-white text-[12px]"
                data-testid="ic-mis-search"
              />
              {query && (
                <button onClick={() => setQuery("")} className="absolute right-4 top-3.5 text-emerald-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto" data-testid="ic-mis-options">
              {filtered.length === 0 ? (
                <div className="text-center py-6 text-[11px] text-emerald-700/70" data-testid="ic-mis-no-results">No matching property in MIS.</div>
              ) : (
                filtered.map((p) => {
                  const isClosed = p.is_operational === false;
                  const isSel = selectedLocs.includes(p.location);
                  return (
                    <button
                      key={p.location}
                      type="button"
                      onClick={() => toggleLoc(p.location)}
                      data-testid={`ic-mis-option-${p.location}`}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] border-b border-emerald-50 last:border-b-0 transition-colors ${isSel ? "bg-emerald-50" : "hover:bg-emerald-50/60"}`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSel ? "bg-emerald-600 border-emerald-600" : "border-zinc-300 bg-white"}`}>
                        {isSel && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className={`flex-1 min-w-0 truncate ${isClosed ? "text-zinc-700" : "text-emerald-900"}`}>{p.location}</span>
                      {isClosed && <span className="text-[9px] uppercase tracking-wide font-bold text-zinc-500 bg-zinc-100 border border-zinc-200 rounded px-1 py-px flex-shrink-0">closed</span>}
                      <span className={`text-[10px] flex-shrink-0 ${isClosed ? "text-zinc-500" : "text-emerald-600"}`}>{p.beds || "—"}b</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected detail */}
      {picked && (
        <div className="mt-3" data-testid="ic-mis-selected">
          <div className="flex items-center justify-between mb-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="font-display font-bold text-[12.5px] text-emerald-900 truncate">{picked.location}</div>
                {picked.is_operational === false && (
                  <span className="text-[9.5px] uppercase tracking-wide font-bold text-zinc-600 bg-zinc-100 border border-zinc-200 rounded px-1.5 py-px flex-shrink-0" data-testid="ic-mis-closed-badge">closed</span>
                )}
              </div>
              <div className="text-[10.5px] text-emerald-700">
                {picked.is_multi ? (
                  <>Averaged across {selectedLocs.length} · <b>{picked._scope}</b></>
                ) : (
                  <>
                    {picked.type || "—"} · {picked.model || "—"} · {picked.beds || "—"} beds
                    {picked.start_date ? ` · since ${picked.start_date}` : ""}
                    {picked.fy_range ? ` · ${picked.fy_range}` : ""}
                    {picked.is_operational === false && picked.last_seen_month ? ` · last seen ${picked.last_seen_month}` : ""}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Year & Month filter chips — single-select only */}
          {!picked.is_multi && history && (history.fy_list || []).length > 0 && (
            <div className="mb-2 -mx-1" data-testid="ic-mis-period-filter">
              <div className="text-[9.5px] uppercase font-bold text-emerald-700 tracking-wide px-1 mb-1.5">Period filter</div>
              <div className="flex flex-wrap gap-1 px-1 mb-1.5">
                <button
                  onClick={() => { setPickedFY(null); setPickedMonth(null); }}
                  data-testid="ic-mis-fy-all"
                  className={`text-[10.5px] px-2 py-0.5 rounded-full border ${!pickedFY ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-emerald-800 border-emerald-300 hover:bg-emerald-50"}`}
                >
                  Last 4 FY avg
                </button>
                {history.fy_list.slice(0, 5).map((fy) => (
                  <button
                    key={fy}
                    onClick={() => { setPickedFY(fy); setPickedMonth(null); }}
                    data-testid={`ic-mis-fy-${fy.replace(/\s/g, "")}`}
                    className={`text-[10.5px] px-2 py-0.5 rounded-full border ${pickedFY === fy && !pickedMonth ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-emerald-800 border-emerald-300 hover:bg-emerald-50"}`}
                  >
                    {fy}
                  </button>
                ))}
              </div>
              {pickedFY && fyMonths.length > 0 && (
                <div className="flex flex-wrap gap-1 px-1" data-testid="ic-mis-month-list">
                  {fyMonths.map((m) => {
                    const active = pickedMonth?.year === m.year && pickedMonth?.month === m.month;
                    return (
                      <button
                        key={`${m.year}-${m.month}`}
                        onClick={() => setPickedMonth(active ? null : { year: m.year, month: m.month })}
                        data-testid={`ic-mis-month-${m.key}`}
                        className={`text-[10px] px-1.5 py-0.5 rounded border tabular-nums ${active ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50"}`}
                      >
                        {m.month_label.replace(" ", " ")}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="text-[10px] text-emerald-700/80 mt-1.5 px-1">
                Showing: <b>{picked?._scope || "Last 4 FY avg"}</b>
                {pickedMonth && (
                  <button onClick={() => setPickedMonth(null)} className="ml-2 text-emerald-700 underline">clear month</button>
                )}
              </div>
            </div>
          )}

          {/* Summary metric cards — dorm + private + revenue */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="text-[9.5px] uppercase font-medium text-emerald-700 tracking-wide">Dorm Occ%</div>
              <div className="font-display font-black text-[15px] text-emerald-900">{picked.dorm_occ_pct ? `${picked.dorm_occ_pct}%` : `${picked.avg_occupancy_pct || "—"}%`}</div>
            </div>
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="text-[9.5px] uppercase font-medium text-emerald-700 tracking-wide">Dorm ADR / bed</div>
              <div className="font-display font-black text-[15px] text-emerald-900">{picked.dorm_adr ? `₹${picked.dorm_adr}` : `₹${picked.avg_adr || "—"}`}</div>
            </div>
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="text-[9.5px] uppercase font-medium text-emerald-700 tracking-wide">Private Occ%</div>
              <div className="font-display font-black text-[15px] text-emerald-900">{picked.private_occ_pct ? `${picked.private_occ_pct}%` : "—"}</div>
            </div>
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="text-[9.5px] uppercase font-medium text-emerald-700 tracking-wide">Private ADR / room</div>
              <div className="font-display font-black text-[15px] text-emerald-900">{picked.private_adr ? `₹${picked.private_adr}` : "—"}</div>
              {picked.private_adr_bed ? <div className="text-[9.5px] text-emerald-600 mt-0.5">₹{picked.private_adr_bed}/bed × 2</div> : null}
            </div>
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="text-[9.5px] uppercase font-medium text-emerald-700 tracking-wide">RevPAB</div>
              <div className="font-display font-black text-[15px] text-emerald-900">₹{picked.avg_revpab || "—"}</div>
            </div>
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="text-[9.5px] uppercase font-medium text-emerald-700 tracking-wide">F&B RevPAB</div>
              <div className="font-display font-black text-[15px] text-emerald-900">₹{picked.non_bed_revpab || "—"}</div>
            </div>
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="text-[9.5px] uppercase font-medium text-emerald-700 tracking-wide">Total Revenue</div>
              <div className="font-display font-black text-[15px] text-emerald-900">{picked.avg_total_revenue_lakhs ? `₹${picked.avg_total_revenue_lakhs}L` : "—"}</div>
            </div>
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="text-[9.5px] uppercase font-medium text-emerald-700 tracking-wide">Margin (Rev − Exp)</div>
              <div className="font-display font-black text-[15px] text-emerald-900">{picked.avg_margin_lakhs ? `₹${picked.avg_margin_lakhs}L` : "—"}</div>
            </div>
          </div>
          {(picked.dorm_adr || picked.avg_adr) && (
            <button
              onClick={() => onApply(picked)}
              data-testid="ic-mis-apply"
              className="mt-2 text-[11px] font-medium bg-emerald-700 text-white px-3 py-1.5 rounded hover:bg-emerald-800 inline-flex items-center gap-1.5"
            >
              <Wand2 className="w-3 h-3" /> Apply MIS averages to all members
            </button>
          )}
        </div>
      )}
      {!picked && (
        <div className="mt-3 text-[11.5px] text-emerald-700/80 text-center py-3 bg-white/60 border border-dashed border-emerald-200 rounded" data-testid="ic-mis-empty">
          Select one or more locations from the dropdown above to see their MIS metrics.
        </div>
      )}
      <MisAskBox />
    </div>
  );
}

// ---- MIS Ask Box (Claude-powered Q&A on MIS data) ----
function MisAskBox() {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ask = async () => {
    const question = q.trim();
    if (!question) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    try {
      const { data } = await api.post("/mis/ask", { question });
      setAnswer(data?.answer || "No answer.");
    } catch (e) {
      logError("mis-ask failed", e);
      setError(e?.response?.data?.detail || "AI unavailable. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) ask();
  };

  return (
    <div className="mt-3 rounded-lg border border-emerald-300 bg-white p-3" data-testid="ic-mis-ask">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
        <span className="font-display font-bold text-[12px] text-emerald-900">Ask MIS · AI</span>
        <span className="text-[10px] text-emerald-700">e.g. “Rent/bed/day for Delhi?” or “Top 3 ADR cities”</span>
      </div>
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask anything about goSTOPS network performance…"
          className="h-8 rounded-md border-emerald-200 text-[12px]"
          data-testid="ic-mis-ask-input"
          disabled={loading}
        />
        <button
          onClick={ask}
          disabled={loading || !q.trim()}
          data-testid="ic-mis-ask-submit"
          className="text-[11px] font-bold bg-emerald-700 text-white px-3 rounded hover:bg-emerald-800 disabled:opacity-60 inline-flex items-center gap-1"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Ask
        </button>
      </div>
      {error && (
        <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="ic-mis-ask-error">
          {error}
        </div>
      )}
      {answer && !error && (
        <div className="mt-2 text-[12px] leading-relaxed text-zinc-800 bg-emerald-50/60 border border-emerald-200 rounded px-3 py-2 whitespace-pre-wrap" data-testid="ic-mis-ask-answer">
          {answer}
        </div>
      )}
    </div>
  );
}
